import getTableId from '../../utils/tables/getTableId';
import listViewStore from '../../store/Store';
import MailFolderTableQuery from '../../store/schema/MailFolderTableQuery';
import TableQuery from '../../store/schema/TableQuery';
import TableView from '../../store/schema/TableView';
import { action, select } from 'satcheljs/lib/legacy';
import { ObservableMap } from 'mobx';

export interface GetTableViewFromTableQueryState {
    tableViews: ObservableMap<string, TableView>;
}

/**
 * Create empty table view based on the table query
 * @param id the table id
 * @param tableQuery the table query
 * @return the table view
 */
function createTableView(id: string, tableQuery: TableQuery): TableView {
    const tableView: TableView = {
        currentLoadedIndex: 0,
        id: id,
        isConversationView: true,
        isInCheckedMode: false,
        isInVirtualSelectAllMode: false,
        virutalSelectAllExclusionList: [],
        isInitialLoadComplete: false,
        hasErrorLoadingTable: false,
        isLoading: true,
        itemIds: [],
        focusInfo: { shouldFocusInMailList: false, focusedRowId: null },
        lastEmptiedTime: null,
        selectedItemsIds: new ObservableMap<string, boolean>(),
        serverFolderId: tableQuery.folderId,
        tableQuery: tableQuery,
        totalRowsInView: 0,
        mailHeaderElementToFocus: (tableQuery as MailFolderTableQuery)?.mailHeaderElementToFocus,
    };

    return tableView;
}

/**
 * Add the table view to store
 * @param tableView the table view
 * @param tableViews the table views collection in the store
 */
const addTableViewToStore = action('addTableViewToStore')(function addTableViewToStore(
    tableView: TableView,
    tableViews: ObservableMap<string, TableView>
) {
    tableViews.set(tableView.id, tableView);
});

/**
 * Get the table view from the list view store based on the table query
 * @param tableQuery the table query
 * @param state which contains all the table views in list view store
 * @return tableView the tableView
 */
export default select({
    tableViews: () => listViewStore.tableViews,
})(function getTableViewFromTableQuery(
    tableQuery: TableQuery,
    state?: GetTableViewFromTableQueryState
): TableView {
    const tableViews = state.tableViews;

    const tableId = getTableId(tableQuery);
    let tableView = tableViews.get(tableId);
    const mailFolderTableQuery = tableQuery as MailFolderTableQuery;
    const mailHeaderElementToFocus = mailFolderTableQuery?.mailHeaderElementToFocus;

    // Create tableView if it doesn't exist yet
    if (!tableView) {
        tableView = createTableView(tableId, tableQuery);
        addTableViewToStore(tableView, tableViews);
    }

    if (mailHeaderElementToFocus) {
        tableView.mailHeaderElementToFocus = mailHeaderElementToFocus;
    }

    return tableView;
});
